import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { isEmpty, isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDatatableData } from '@actions/data-table-actions';
import {
  ENTITY_DATA_TYPE,
  OVERLAP_DATA_TYPE
} from '@components/config/constants';
import Filter from '@components/filters';
import { getDataTypeFilterParams } from '@components/filters/selectors';
import { getDataTableActions } from '@constants/config';
import {
  getDataTableColumns,
  getFilters,
  getLoader,
  optimizeRowsForDataTable
} from '@selectors/data-table-selector';
import Table from '@shared/table';
import ActionsMenu from '@utils/data-table/column-renderers/actions-menu';
import { getEmptyLabel, onRowClick } from '@utils/data-table-utils';
import Footer from './footer';
import Header from './header';
import './data-table.scss';

// Convert URL params to normalized types for selectors.
// Returns { dataType, subType } matching DATA_TYPES structure.
const getTypes = (params) => {
  const { baseType, dataType, subType } = params;
  // Admin routes use baseType (from /admin/data/:dataType URL structure)
  if (baseType) {
    return {
      dataType: baseType === 'overlaps' ? 'overlap' : baseType,
      subType: dataType
    };
  }
  // Library/Workflow/People routes use dataType and subType directly
  return { dataType, subType };
};

// Convert to legacy types for API calls (same logic as Portal's useLegacyTypes).
// For entity types, the API uses just the entity type name (e.g., 'project').
const getLegacyTypes = (dataType, subType) => {
  if (dataType === ENTITY_DATA_TYPE) {
    // entity/project -> legacyDataType='project', legacySubType=null
    return { legacyDataType: subType, legacySubType: null };
  }
  if (dataType === OVERLAP_DATA_TYPE) {
    // overlap/spatial -> legacyDataType='overlap', legacySubType='spatial'
    return { legacyDataType: 'overlap', legacySubType: subType };
  }
  // Other types stay the same
  return { legacyDataType: dataType, legacySubType: subType };
};

const useDataTable = (dataType, subType, legacyDataType, legacySubType) => {
  // Use original types for filter selectors (they use DATA_TYPES lookup)
  const filterParams = useSelector(state => getDataTypeFilterParams(state, dataType, subType));
  // Use legacy types for data selectors (data is stored under legacy types)
  const legacyProps = useMemo(() => ({ dataType: legacyDataType, subType: legacySubType }), [legacyDataType, legacySubType]);
  const columns = useSelector(state => getDataTableColumns(state, legacyProps));
  const paginationParams = useSelector(state => getFilters(state, legacyProps));
  const loading = useSelector(state => getLoader(state, legacyProps));
  const rows = useSelector(state => optimizeRowsForDataTable(legacyDataType, legacySubType)(state));

  return { columns, filterParams, loading, paginationParams, rows };
};

const DataTable = () => {
  const dispatch = useDispatch();
  const params = useParams();
  // Get normalized types for selectors/filters (matches DATA_TYPES structure)
  const { dataType, subType } = getTypes(params);
  // Get legacy types for API calls
  const { legacyDataType, legacySubType } = useMemo(
    () => getLegacyTypes(dataType, subType),
    [dataType, subType]
  );
  const { columns, filterParams, loading, paginationParams, rows } = useDataTable(dataType, subType, legacyDataType, legacySubType);

  // Merge filter params (from Filter component) with pagination params (from Paginator)
  const allParams = useMemo(
    () => ({ ...filterParams, ...paginationParams }),
    [filterParams, paginationParams]
  );

  // Track previous params to detect actual changes
  const prevParamsRef = useRef(null);
  const prevTypesRef = useRef({ legacyDataType: null, legacySubType: null });

  useEffect(() => {
    // Only fetch if types or params actually changed
    const typesChanged = prevTypesRef.current.legacyDataType !== legacyDataType ||
                         prevTypesRef.current.legacySubType !== legacySubType;
    const paramsChanged = !isEqual(prevParamsRef.current, allParams);

    if (typesChanged || paramsChanged) {
      prevTypesRef.current = { legacyDataType, legacySubType };
      prevParamsRef.current = allParams;
      dispatch(fetchDatatableData(legacyDataType, legacySubType, allParams));
    }
  }, [legacyDataType, legacySubType, allParams, dispatch]);

  // Get actions config for this data type (if available)
  const actionsConfig = getDataTableActions('list')?.[dataType] || getDataTableActions('admin')?.[dataType];

  // Create actions renderer that uses the existing ActionsMenu component
  const actionsRenderer = useCallback(row => (
    <ActionsMenu
      actions={actionsConfig}
      dataId={row.id}
      dataType={dataType}
      row={row}
    />
  ), [actionsConfig, dataType]);

  if (!isEmpty(columns) && rows) {
    const tableProps = {
      actionsRenderer: actionsConfig ? actionsRenderer : null,
      columns,
      dataType,
      empty: { message: getEmptyLabel(dataType) },
      header: <Header dataType={dataType} subType={subType} />,
      loading,
      // eslint-disable-next-line no-unused-vars
      onRowClick: (event, row, index) => onRowClick(event, row, dataType, subType, dispatch),
      rows,
      search: <Filter dataType={dataType} subType={subType} layout="horizontal" />,
      stickyFirstColumn: true,
      subType
    };

    return (
      <div styleName="data-table-container">
        <Table {...tableProps} />
        <Footer dataType={legacyDataType} subType={legacySubType} />
      </div>
    );
  }
  return null;
};

export default DataTable;
